import { useDeferredValue, useState } from 'react'

import { Box, WizardStepRenderParams, useOnNext } from '@factorialco/deprecated-design-system'
import { F0Card, F0Select } from '@factorialco/f0-react'
import { Input, ScrollArea } from '@factorialco/f0-react/dist/experimental'
import { Pig } from '@factorialco/f0-react/icons/app'
import {
  BankingInstitutionCountriesDocument,
  BankingInstitutionsDocument,
  InstitutionFragment,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { countries } from 'modules/core/lib/countryList'
import i18n from 'modules/core/lib/i18n'

import { WizardData } from 'modules/banking/components/NewAccountModal/types'
import { WizardStepLayout } from 'modules/banking/components/NewAccountModal/wizardSteps/WizardStepLayout'

interface Props {
  onSelect: (institution: InstitutionFragment | null) => void
}

export const BankAccountSelectorWizardStepF0 = <T extends WizardData>({
  data,
  onDataChanged,
  onSelect,
}: WizardStepRenderParams<T> & Props) => {
  const [country, setCountry] = useState<string | undefined>(undefined)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDeferredValue(search)

  const { data: countriesData } = useQuery(BankingInstitutionCountriesDocument)

  const countryOptions = useUnsafeMemo(() => {
    const supportedCountries = new Set(
      countriesData?.banking.institutions.flatMap((inst) => inst.countries) ?? []
    )
    return countries(i18n.locale)
      .filter((c) => supportedCountries.has(c.value.toUpperCase()))
      .map((c) => ({
        value: c.value.toUpperCase(),
        label: c.label,
      }))
  }, [countriesData])

  // As there is another step using this.. we have to override the onNext callback
  useOnNext(() => Promise.resolve())

  const countrySelected = country ?? data.country

  const { data: institutions } = useQuery(BankingInstitutionsDocument, {
    variables: {
      country: country ?? countrySelected ?? '',
      name: debouncedSearch || data.search,
    },
    skip: !country && !countrySelected,
  })

  const nodes = useUnsafeMemo(
    () => institutions?.banking.institutionsConnection.nodes,
    [institutions]
  )

  const handleCountryChange = useUnsafeCallback(
    (value: string) => {
      setCountry(value.toUpperCase())
      onDataChanged({
        ...data,
        country: value.toUpperCase(),
      })
    },
    [data, onDataChanged]
  )

  const handleSearchChange = useUnsafeCallback(
    (value: string) => {
      setSearch(value)
      onDataChanged({
        ...data,
        search: value,
      })
    },
    [data, onDataChanged]
  )

  const handleManualSelect = useUnsafeCallback(() => {
    onSelect(null)
    onDataChanged({
      ...data,
      institution: undefined,
    })
  }, [data, onDataChanged, onSelect])

  const handleInstitutionSelect = useUnsafeCallback(
    (institution: InstitutionFragment) => {
      onSelect(institution)
      onDataChanged({
        ...data,
        institution,
      })
    },
    [data, onDataChanged, onSelect]
  )

  return (
    <WizardStepLayout
      title={i18n.t('banking.accounts.new_wizard.step_2.title')}
      subtitle={i18n.t('banking.accounts.new_wizard.step_2.subtitle')}
    >
      <Box style={{ width: '500px' }} flexDirection="column" gap="s16">
        <Box flexDirection="row" gap="s8" paddingX="s12">
          <Box style={{ width: '160px' }}>
            <F0Select
              placeholder={i18n.t('common.select')}
              options={countryOptions}
              value={countrySelected?.toUpperCase()}
              onChange={handleCountryChange}
            />
          </Box>
          <Box style={{ flex: '1' }}>
            <Input
              label={i18n.t('common.search')}
              hideLabel
              disabled={!countrySelected}
              placeholder={i18n.t('common.search')}
              value={(search || data.search) ?? ''}
              onChange={handleSearchChange}
            />
          </Box>
        </Box>

        <ScrollArea style={{ height: '340px', marginTop: '-12px' }}>
          <Box flexDirection="column" gap="s8" padding="s12">
            {/* Manual option */}
            <F0Card
              description={i18n.t('banking.accounts.new_wizard.add_manually')}
              avatar={{ type: 'icon', icon: Pig }}
              onClick={handleManualSelect}
              selected={data.institution === undefined}
              compact
            />

            {/* Institution list */}
            {nodes?.map((institution) => (
              <F0Card
                key={institution.id}
                title={institution.name}
                avatar={{ type: 'company', name: institution.name, src: institution.logo }}
                onClick={() => handleInstitutionSelect(institution)}
                selected={data.institution?.id === institution.id}
                compact
              />
            ))}
          </Box>
        </ScrollArea>
      </Box>
    </WizardStepLayout>
  )
}
